
import { Component, Vue, Prop } from 'vue-property-decorator';
import { StoreModel } from '@/models/store';
import { AutomationConfigList } from '@/collections/automation_configs';
import { AutomationConfigModel } from '@/models/automation_config';
import { SupplierList } from '@/collections/suppliers';
import { SupplierModel } from '@/models/supplier';
import dateFormat from 'dateformat';
import { EventBus } from '@/main';
import AutomationConfigForm from '@/components/purchase-order/AutomationConfigForm.vue';
import { EventHandler } from '@/modules/events';
import { getFeatureVersion, setFeatureVersion } from '@/config/atlasFeatures';
import TryNewAtlasBanner from '@/components/TryNewAtlasBanner.vue';
import CopyLinkButton from '@/components/CopyLinkButton.vue';

/**
 * The main view for managing Automated Purchase Order Configurations.
 * It displays a filterable, sortable list of configurations and provides
 * controls for creating, editing, deleting, and toggling them.
 */
@Component({
  components: {
    AutomationConfigForm,
    TryNewAtlasBanner,
    CopyLinkButton,
  },
})
export default class AutomatedPOConfig extends Vue {
  /** The application's central store model. */
  @Prop({ default: () => new StoreModel() }) store!: StoreModel;

  /** The collection that holds and manages the list of configurations. */
  private configsCollection = new AutomationConfigList();
  
  /** Toggles the loading state of the data table. */
  private loading = false;

  /** The search term entered by the user. */
  private search = '';

  /** An object containing the current filter values. */
  private filters = {
    status: '' as 'active' | 'inactive' | '',
    supplierId: null as number | null,
  };

  /** The Vuetify data table options (pagination, sorting). */
  private options = {
    page: 1,
    itemsPerPage: 10,
    sortBy: ['createdAt'],
    sortDesc: [true]
  };

  /** The header definitions for the data table. */
  private headers = [
    { text: 'Configuration Name', value: 'name', width: '25%', sortable: false },
    { text: 'Supplier', value: 'supplier.name', sortable: false },
    { text: 'Schedule', value: 'schedule', sortable: false },
    { text: 'Run Times', value: 'nextRunTime', sortable: false },
    { text: 'Status', value: 'isActive', sortable: false, align: 'center' },
    { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '150px' }
  ];
  
  /** The list of available suppliers for the filter dropdown. */
  private suppliers: SupplierModel[] = [];

  /** Toggles the loading state of the supplier filter. */
  private loadingSuppliers = false;
  
  // Dialog states
  /** Controls the visibility of the delete confirmation dialog. */
  private deleteDialog = false;

  /** Controls the visibility of the create/edit form dialog. */
  private formDialog = false;

  /** Holds the configuration currently selected for an action (edit/delete). */
  private selectedConfig: AutomationConfigModel | null = null;

  /** Toggles the loading state of the delete button. */
  private deleting = false;

  /** A set of IDs for configurations that are currently being updated (toggled). */
  private updating = new Set<string>();

  /** A set of IDs for configurations that have recently completed API calls. */
  private recentlyCompleted = new Set<string>();

  /** Error message to display in a dialog */
  private errorDialog = false;
  private errorMessage = '';

  /** Success message to display */
  private successDialog = false;
  private successMessage = '';

  /** The options for the status filter dropdown. */
  private statusOptions = [
    { text: 'Active', value: 'active' },
    { text: 'Inactive', value: 'inactive' },
  ];

  get showTryNewBanner(): boolean {
    return getFeatureVersion('automatedPO') === 'classic';
  }

  switchToAtlas(): void {
    setFeatureVersion('automatedPO', 'atlas');
    this.$router.push('/assistant/automated-po');
  }

  /**
   * Computed property to get the list of configurations from the collection.
   * @returns {AutomationConfigModel[]} The list of configurations.
   */
  get configs(): AutomationConfigModel[] {
    return this.configsCollection.items;
  }

  /**
   * Computed property to get the total number of configurations for pagination.
   * @returns {number} The total number of configurations.
   */
  get totalConfigs(): number {
    return this.configsCollection.total;
  }

  /**
   * Vue lifecycle hook that is called after the instance has been created.
   */
  async created() {
    // Track page view for automation configs page
    const eventHandler = new EventHandler({
      store: this.store,
    });
    eventHandler.trackAutomatedPurchaseOrderStep('Page Viewed', {
      page: 'automation-configs'
    });
    
    await this.fetchConfigs();
    await this.fetchSuppliers();
  }

  /**
   * Fetches the list of configurations from the server based on current filters and pagination.
   * @private
   */
  private async fetchConfigs() {
    this.loading = true;
    try {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      
      const statusFilter = this.filters.status 
        ? this.filters.status === 'active'
        : undefined;

      await this.configsCollection.fetch({
        page,
        limit: itemsPerPage,
        sort_by: sortBy[0] || 'createdAt',
        sort_order: sortDesc[0] ? 'DESC' : 'ASC',
        q: this.search,
        is_active: statusFilter,
        supplier_id: this.filters.supplierId || undefined,
      });
    } catch (error: any) {
      EventBus.$emit('show-snackbar', {
        message: `Error fetching configurations: ${error.message}`,
        color: 'error',
        timeout: 5000
      });
    } finally {
      this.loading = false;
    }
  }

  /**
   * Fetches the list of suppliers for the filter dropdown.
   * @private
   */
  private async fetchSuppliers() {
    this.loadingSuppliers = true;
    try {
      const supplierCollection = new SupplierList();
      await supplierCollection.fetch();
      this.suppliers = supplierCollection.items;
    } catch (error: any) {
      EventBus.$emit('show-snackbar', {
        message: `Error fetching suppliers: ${error.message}`,
        color: 'error',
        timeout: 5000
      });
    } finally {
      this.loadingSuppliers = false;
    }
  }

  /**
   * Computed property to generate the items for the supplier filter dropdown.
   * @returns {object[]} The list of supplier options.
   */
  get supplierOptions() {
    return [
      { id: null, name: 'All Suppliers' },
      ...this.suppliers,
    ];
  }

  /**
   * Formats the schedule details into a human-readable string.
   * @param {AutomationConfigModel} item - The configuration item.
   * @returns {string} A formatted schedule string.
   */
  private formatSchedule(item: AutomationConfigModel): string {
    const { scheduleType, scheduleConfig } = item;
    if (!scheduleType || !scheduleConfig || scheduleConfig.hour === undefined) return 'Not configured';

    const timeStr = `${String(scheduleConfig.hour).padStart(2, '0')}:00`;

    switch (scheduleType) {
      case 'daily':
        return `Daily at ${timeStr}`;
      case 'weekly': {
        if (scheduleConfig.dayOfWeek === undefined) return 'Weekly (incomplete)';
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        return `Weekly on ${days[scheduleConfig.dayOfWeek]} at ${timeStr}`;
      }
      case 'monthly': {
        if (scheduleConfig.dayOfMonth === undefined) return 'Monthly (incomplete)';
        const dayOfMonthStr = scheduleConfig.dayOfMonth === -1 ? 'the last day' : `day ${scheduleConfig.dayOfMonth}`;
        return `Monthly on ${dayOfMonthStr} at ${timeStr}`;
      }
      default:
        return 'Invalid schedule';
    }
  }

  /**
   * Formats a date string into a more readable format.
   * @param {string | Date} date - The date to format.
   * @returns {string} A formatted date string.
   */
  private formatDate(date: string | Date): string {
    if (!date) return '';
    return dateFormat(date, "mmm d, yyyy 'at' h:MM TT");
  }

  /**
   * Opens the form dialog in 'create' mode.
   * @private
   */
  private openCreateDialog() {
    // Track new configuration button click
    const eventHandler = new EventHandler({
      store: this.store,
    });
    eventHandler.trackAutomatedPurchaseOrderStep('New Configuration Clicked', {
      page: 'automation-configs',
      action: 'open_create_dialog'
    });
    
    this.selectedConfig = null;
    this.formDialog = true;
  }

  /**
   * Opens the form dialog in 'edit' mode for the given item.
   * @param {AutomationConfigModel} item - The item to edit.
   * @private
   */
  private editConfig(item: AutomationConfigModel) {
    this.selectedConfig = item;
    this.formDialog = true;
  }

  /**
   * Opens the delete confirmation dialog for the given item.
   * @param {AutomationConfigModel} item - The item to delete.
   * @private
   */
  private confirmDelete(item: AutomationConfigModel) {
    this.selectedConfig = item;
    this.deleteDialog = true;
  }

  /**
   * Deletes the currently selected configuration.
   * @private
   */
  private async deleteConfig() {
    if (!this.selectedConfig || !this.selectedConfig.id) return;
    
    this.deleting = true;
    try {
      const modelToDelete = new AutomationConfigModel({ id: this.selectedConfig.id });
      await modelToDelete.delete();
      EventBus.$emit('show-snackbar', {
        message: 'Configuration deleted successfully.',
        color: 'success',
        timeout: 4000
      });
      this.deleteDialog = false;
      await this.fetchConfigs();
    } catch (error: any) {
      EventBus.$emit('show-snackbar', {
        message: `Error deleting configuration: ${error.message}`,
        color: 'error',
        timeout: 5000
      });
    } finally {
      this.deleting = false;
    }
  }

  /**
   * Toggles the active status of a configuration.
   * @param {AutomationConfigModel} item - The item to toggle.
   * @private
   */
  private async toggleConfig(item: AutomationConfigModel) {
    if (!item.id) return;
    
    this.updating.add(String(item.id));
    this.$forceUpdate();

    try {
      const model = new AutomationConfigModel(item.toJSON());
      await model.save(); // Save the whole model to toggle isActive
      EventBus.$emit('show-snackbar', {
        message: `Configuration "${item.name}" has been ${item.isActive ? 'activated' : 'deactivated'}.`,
        color: 'success',
        timeout: 4000
      });
    } catch (error: any) {
      EventBus.$emit('show-snackbar', {
        message: `Error updating configuration: ${error.message}`,
        color: 'error',
        timeout: 5000
      });
      // Revert state on failure
      item.isActive = !item.isActive;
    } finally {
      this.updating.delete(String(item.id));
      this.$forceUpdate();
    }
  }

  /**
   * Checks if a specific configuration is currently being updated.
   * @param {number} itemId - The ID of the item to check.
   * @returns {boolean} True if the item is being updated.
   * @private
   */
  private isUpdating(itemId: number): boolean {
    return this.updating.has(String(itemId));
  }

  /**
   * Checks if a specific configuration is currently running a manual test.
   * @param {number | undefined} itemId - The ID of the item to check.
   * @returns {boolean} True if the item is running a manual test.
   * @private
   */
  private isManualRunning(itemId: number | undefined): boolean {
    if (!itemId) return false;
    return this.updating.has(`manual-${itemId}`);
  }

  /**
   * Checks if a specific configuration has recently completed an API call.
   * @param {number | undefined} itemId - The ID of the item to check.
   * @returns {boolean} True if the item recently completed an API call.
   * @private
   */
  private isRecentlyCompleted(itemId: number | undefined): boolean {
    if (!itemId) return false;
    return this.recentlyCompleted.has(`manual-${itemId}`);
  }

  /**
   * Handles the save event from the form dialog. Creates or updates a configuration.
   * @param {AutomationConfigModel} configData - The configuration data from the form.
   * @private
   */
  private async saveConfig(configData: AutomationConfigModel | any) {
    // Ensure configData is an instance of AutomationConfigModel
    let modelInstance: AutomationConfigModel;
    if (configData instanceof AutomationConfigModel) {
      modelInstance = configData;
    } else {
      // If it's a plain object (e.g., from SchedulePicker), create a new model instance
      modelInstance = new AutomationConfigModel(configData);
    }
    
    const isNew = !modelInstance.id;
    try {
      await modelInstance.save();
      const message = isNew
        ? 'Configuration created successfully.'
        : 'Configuration updated successfully.';
      EventBus.$emit('show-snackbar', {
        message,
        color: 'success',
        timeout: 4000
      });
      this.formDialog = false;
      await this.fetchConfigs();
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      EventBus.$emit('show-snackbar', {
        message: `Failed to save configuration: ${error.message}`,
        color: 'error',
        timeout: 6000
      });
    }
  }

  /**
   * Computed property to check if the user has permission to manage automations.
   * @returns {boolean} True if the user has permission.
   */
  get canManageAutomation(): boolean {
    // Check if user has permission to manage automation
    const validateValues = [
      {
        code: 'operational_mode',
        value: 'unlocked'
      }
    ];
    const validateResult = this.store.validateRule(validateValues);
    return validateResult.isValidate;
  }

  /**
   * Clears all active filters and re-fetches the configuration list.
   * @private
   */
  private clearFilters() {
      this.search = '';
      this.filters = {
          status: '',
          supplierId: null,
      };
      this.fetchConfigs();
  }

  /**
   * Triggers a manual run of the specified configuration.
   * @param {AutomationConfigModel} item - The configuration to run.
   * @private
   */
  private async runNow(item: AutomationConfigModel) {
    if (!item.id) return;
    
    // Show immediate feedback with loading snackbar
    EventBus.$emit('show-snackbar', {
      message: `Sending request to start manual run for "${item.name}"...`,
      color: 'info',
      timeout: 2000,
      icon: 'mdi-send'
    });
    
    // Add to updating set to show loading state on button
    this.updating.add(`manual-${item.id}`);
    this.$forceUpdate();
    
    try {
      // Call the API endpoint
      const response = await item.testRun();
      
      // API call completed successfully - show detailed success message
      const successMsg = `✅ The manual run for "${item.name}" has been successfully initiated.\n\n` +
        `The purchase order generation is now in progress and will complete in approximately ${response.estimatedTime || '1-2 minutes'}.\n\n` +
        `You will receive an email notification when the PO is ready.` +
        (response.jobId ? `\n\nJob ID: ${response.jobId}` : '');
      
      // Show success in multiple ways to ensure visibility
      // 1. Show success dialog
      this.successMessage = successMsg;
      this.successDialog = true;
      
      // 2. Also show snackbar
      EventBus.$emit('show-snackbar', {
        message: `✅ Manual run started successfully for "${item.name}"!`,
        color: 'success',
        timeout: 5000,
        icon: 'mdi-check-circle'
      });

      // Mark as recently completed for visual feedback
      this.recentlyCompleted.add(`manual-${item.id}`);
      this.$forceUpdate();
      
      // Log success for debugging
      
      // 4. Browser notification as additional feedback (if permitted)
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('✅ Manual Run Started', {
          body: `PO generation for "${item.name}" has been initiated successfully.`,
          icon: '/favicon.ico'
        });
      }
      
      // Remove the "recently completed" status after a few seconds
      setTimeout(() => {
        this.recentlyCompleted.delete(`manual-${item.id}`);
        this.$forceUpdate();
      }, 3000);
      
      // Track successful manual run
      if (this.store) {
        const eventHandler = new EventHandler({ store: this.store });
        eventHandler.trackAutomatedPurchaseOrderStep('Manual Run Triggered', {
          configId: item.id,
          configName: item.name,
          supplierId: item.supplierId,
          jobId: response.jobId || null
        });
      }
      
      // Update the item's lastRunTime locally for immediate UI feedback
      if (item.lastRunTime !== undefined) {
        item.lastRunTime = new Date().toISOString();
      }
      
      // Refresh the list after a delay to show updated status from server
      setTimeout(() => {
        EventBus.$emit('show-info', `Checking status for "${item.name}"...`);
        this.fetchConfigs();
      }, 5000);
      
    } catch (error: any) {
      // Log error for debugging
      console.error('Error calling test-run API:', error);
      console.error('Error response:', error.response);
      
      // Ensure we always show an error message to the user
      const errorStatus = error.response?.status;
      let errorMessage = '';
      
      // Handle specific error cases with clearer messages
      if (errorStatus === 409) {
        errorMessage = `⚠ A purchase order generation is already in progress for "${item.name}". ` +
          `Please wait for it to complete before triggering another run.`;
        EventBus.$emit('show-snackbar', {
          message: errorMessage,
          color: 'warning',
          timeout: 6000
        });
      } else if (errorStatus === 400) {
        errorMessage = `✗ Cannot run inactive configuration "${item.name}". ` +
          `Please activate the configuration first using the toggle switch.`;
        EventBus.$emit('show-snackbar', {
          message: errorMessage,
          color: 'error',
          timeout: 6000
        });
      } else if (errorStatus === 404) {
        errorMessage = `✗ Configuration "${item.name}" was not found. ` +
          `Please refresh the page and try again.`;
        EventBus.$emit('show-snackbar', {
          message: errorMessage,
          color: 'error',
          timeout: 6000
        });
      } else if (errorStatus === 500) {
        // For 500 errors, try to extract more details from the response
        const serverErrorDetails = error.response?.data?.error || error.response?.data?.message || 'Internal server error';
        errorMessage = `❌ Server Error: Failed to start manual run for "${item.name}".\n\n` +
          `Details: ${serverErrorDetails}\n\n` +
          `Please try again in a few moments or contact support if this continues.`;
        
        // Show error as snackbar
        EventBus.$emit('show-snackbar', {
          message: errorMessage,
          color: 'error',
          timeout: 8000
        });

        // Also show details in a separate snackbar
        EventBus.$emit('show-snackbar', {
          message: `Server error: ${serverErrorDetails}`,
          color: 'error',
          timeout: 8000,
          icon: 'mdi-alert-circle'
        });
        
        // 3. Show error dialog as fallback for 500 errors
        this.errorMessage = errorMessage;
        this.errorDialog = true;
      } else {
        // Generic error handling
        errorMessage = `✗ Failed to trigger manual run for "${item.name}": ${error.response?.data?.message || error.message || 'Unknown error'}`;
        EventBus.$emit('show-snackbar', {
          message: errorMessage,
          color: 'error',
          timeout: 6000
        });
      }
      
      // Always log the error message that was shown to the user
      
      // Fallback: Use native alert if EventBus might not be working
      if (errorStatus === 500 && !this.$root.$children[0]) {
        setTimeout(() => {
          if (!document.querySelector('.v-snack__content')) {
            // If no snackbar is visible after 500ms, use alert as fallback
            alert(errorMessage);
          }
        }, 500);
      }
    } finally {
      // Remove from updating set
      this.updating.delete(`manual-${item.id}`);
      this.$forceUpdate();
    }
  }
}
